import { Link } from "react-router";
import { ArrowLeft, Target, Circle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";

export function MaskWorkflowDocPage() {
  return (
    <>
      <Header />
      <Main>
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/docs" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>

          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-linear-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Mask Workflow</h1>
              <p className="text-xl text-gray-600">
                Prepare a mask for design review, test labels, and measurement
              </p>
            </div>
          </div>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              Set up a mask from its project details page, then follow the workflow from design
              upload through test-label validation and measurement requests. On-PCM designs use XML
              uploads, while Off-PCM designs use CSV. The spec-list step is not required for masks
              with only Off-PCM designs.
            </p>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-semibold text-blue-900 mb-2">Open the workflow:</h4>
              <ol className="text-blue-800 text-sm space-y-1">
                <li>
                  1. Open the{" "}
                  <Link to="/projects" className="underline font-medium">
                    Projects
                  </Link>{" "}
                  page.
                </li>
                <li>2. Select a project to open its details.</li>
                <li>3. Select a mask, or click Add Mask to create one.</li>
                <li>
                  4. After creation, Photon Flow selects the new mask and opens its details page.
                </li>
              </ol>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-900 mb-2">Prerequisites:</h4>
              <ul className="text-yellow-800 text-sm space-y-1">
                <li>• A project with a mask selected, or permission to add one.</li>
                <li>• Decide whether this mask will use XML designs, CSV designs, or both.</li>
                <li>
                  • On-PCM test labels use project specs; Off-PCM test labels use design structures.
                </li>
                <li>• A successful test flight validates a label and unlocks Step 6.</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-8">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Step 1: Mask Details</CardTitle>
                  <p className="text-sm text-gray-600">Create a mask or review its setup</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                Click <strong>Add Mask</strong> on the project details page to open the mask form.
                Enter the mask name and PCM version, then provide its wafer and die details. After
                saving, Photon Flow selects the new mask and opens its details page so you can
                continue with the remaining steps. Existing masks display their current setup here;
                authorized users can edit it.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Step 2: Design Upload</CardTitle>
                  <p className="text-sm text-gray-600">
                    Choose the design flow and upload its files
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Choose a tab to upload designs for the selected mask. Use <strong>On PCM</strong>
                for official designs and upload a ZIP archive containing the design XML files. Use
                <strong> Off PCM</strong> for custom measurements directly on the die and upload a
                CSV file. You can use both tabs when the mask has both kinds of designs.
              </p>
              <p className="text-gray-700 mb-4">
                Upload successive files to add designs or update an existing design set. When
                updating, review the change preview before applying it; uploads are recorded in the
                design changelog. Design name, test site name, and sub-die name cannot be changed.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">File validation:</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>
                    For an on-PCM update, include an XML for every design from the previous upload.
                    The update is invalid if any of those designs is missing.
                  </li>
                  <li>CSV uploads do not have this matching-file requirement.</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Step 3: Chip Position Files</CardTitle>
                  <p className="text-sm text-gray-600">
                    Create chip positions or start from a preset file
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Click <strong>Create Chip Pos</strong> to select dies on the wafer and save the
                selection as a chip-position file. Alternatively, import a preset{" "}
                <code>.chip_pos</code> file to load its selection into the editor. Adjust the
                selection if needed, then save it. Existing files can be opened to edit or download,
                or deleted when permitted. The file format supports ordered selections (with +
                symbols and numbers) and unordered selections (with X symbols).
              </p>
              <p className="text-gray-700">
                See the{" "}
                <Link to="/docs/chip-pos-file-format" className="text-blue-600 hover:underline">
                  Chip Pos File Format
                </Link>{" "}
                reference for details.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Step 4: Project Spec Lists</CardTitle>
                  <p className="text-sm text-gray-600">
                    Create or link specifications for the mask
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                For PCM designs, create a project spec list here. Choose a derived list to select
                specs from a master version and customize their values, or choose a custom list and
                upload a CSV. The current workflow requires a master version to be available before
                it displays the creation options. Existing lists link to their active versions.
              </p>
              <p className="text-gray-700">
                If the mask has only CSV designs, no project spec list is needed and the workflow
                treats this step as complete. Off-PCM test labels use design structures instead of
                specs.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Step 5: Test Labels</CardTitle>
                  <p className="text-sm text-gray-600">
                    Create labels and validate them with a test flight
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Create a test label by selecting specs for an on-PCM design or structures for an
                off-PCM design. For an unvalidated label, choose <strong>Create Test Flight</strong>{" "}
                to open its measurement-request form. Provide an Imec or Sfab lot ID and add at
                least one wafer. The label becomes validated only after its test flight completes
                successfully.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Test flight:</h4>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Each test flight is linked to the test label it will validate.</li>
                  <li>
                    • Its request name is fixed: <code>[test-flight] {"{testlabelname}"}</code>.
                  </li>
                  <li>• It requires an Imec or Sfab lot ID and at least one wafer.</li>
                  <li>
                    • The label remains unvalidated until the test flight completes successfully.
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Step 6: Measurement Requests</CardTitle>
                  <p className="text-sm text-gray-600">
                    Create additional requests after label validation
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                This step becomes available after at least one test label has been validated. Create
                additional measurement requests for the mask here. Unlike test flights, these
                requests can use a name of your choice, and you can associate any validated test
                label with them. Select a request to open its details.
              </p>
              <p className="text-gray-700">
                To schedule existing requests, see{" "}
                <Link
                  to="/docs/plan-measurement-requests"
                  className="text-blue-600 hover:underline"
                >
                  Plan Measurement Requests
                </Link>
                .
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Workflow Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              Select a mask and upload its XML designs, CSV designs, or both. Create chip-position
              files by selecting dies or importing a preset. For PCM designs, create a project spec
              list; skip that requirement for CSV-only masks. Then create a test label using specs
              or structures and complete a successful test flight to validate it. Once a label is
              validated, Step 6 lets you create additional requests. Schedule requests separately in
              Request Planning.
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-2">Workflow Checklist:</h4>
              <ul className="text-green-800 text-sm space-y-1">
                <li>✓ Mask created and selected</li>
                <li>✓ Relevant XML and/or CSV designs uploaded</li>
                <li>✓ Chip-position files created from die selections or imported presets</li>
                <li>✓ Project spec list created for PCM designs, if applicable</li>
                <li>✓ Test labels created and validated by successful test flights</li>
                <li>✓ Additional measurement requests created and ready to schedule</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
